/**
 * O corpo de quem não tem escritório 2D.
 *
 * Num telemóvel o mundo 2D não existe — não há WASD, não há tecla E, e o
 * `OfficeSpace` nunca chega a montar. Mas a pessoa do outro lado existe, e para
 * quem está ao computador ela tem de aparecer no mapa como qualquer outra: senão
 * metade da equipa é invisível para a outra metade, e um escritório onde só se vê
 * quem está ao portátil não é um escritório, é uma sala de espera.
 *
 * A regra que o Kawã pediu, à letra: entra no servidor → está na Recepção; entra
 * numa call → é teletransportado para a sala dessa call.
 *
 * E não é só o telemóvel. Um desktop que carrega em «Discord» fica na mesma
 * situação — corpo no mundo, ninguém a desenhá-lo — e é aqui que ele passa a
 * ser tratado: enquanto a call o segurar numa sala, o corpo fica no mapa e é
 * este ficheiro que o move e que o tira de lá quando a chamada acabar. Ver
 * `corpoNoMapa`.
 *
 * Nada disto é um modo especial do protocolo. É o MESMO `EnterSpace` /
 * `UpdatePosition` / `LeaveSpace` que o escritório usa desde sempre — só que a
 * posição vem de uma regra em vez de vir do teclado. O servidor não sabe (nem
 * precisa de saber) que do outro lado está um telemóvel, e o cliente de desktop
 * desenha este avatar com o mesmo código com que desenha os outros.
 *
 * O salto de sala não precisa de licença nenhuma: `canEnter` só guarda salas
 * TRANCADAS, não paredes — e é bom que guarde. Se a call for numa sala trancada
 * de onde este telemóvel não vem, o servidor recusa e devolve a posição anterior,
 * exactamente como recusaria a um avatar a andar. A privacidade da sala vale mais
 * do que a presença de quem está na call, e é o servidor que decide isso.
 */

import type { Channel, Snowflake } from "@greenvoice/shared";
import { roomAt, roomByVoiceName, SPAWN, standingSpotIn } from "@greenvoice/shared";
import { useEffect } from "react";

import { corpoLembrado } from "../components/officeBody";
import { useIsPhone } from "../lib/media";
import { gatewayClient, useStore, type VoiceSession } from "../store/store";

/**
 * Em que SALA do escritório é que este cliente deve aparecer — ou `null` para a
 * Recepção, que é o sítio de quem está no servidor sem estar em call nenhuma.
 *
 * Pura de propósito, e exportada: é aqui que vive a decisão toda, e uma decisão
 * que se testa com três objectos literais vale mais do que a mesma decisão
 * enterrada dentro de um `useEffect` que precisa de um browser para correr.
 *
 * Devolve `string | null` e não um ponto: um selector do zustand que construa
 * `{x, y}` a cada leitura devolve um objecto novo de cada vez, e um objecto novo
 * de cada vez é um render infinito. O ponto calcula-se depois, uma vez.
 *
 * Três razões para ficar na Recepção, todas legítimas e nenhuma um erro:
 *   • não está em call;
 *   • está em call NOUTRO servidor (o escritório é do servidor activo, e o
 *     avatar pertence ao escritório que se está a ver — não ao da conversa);
 *   • está numa call que não é sala nenhuma ("Sala Principal", "Partilha de
 *     Ecrã"). Inventar-lhe um sítio no mapa era pior do que a Recepção.
 */
export function presenceRoomId(
  activeServerId: Snowflake | null,
  voice: VoiceSession | null,
  channels: Record<Snowflake, Channel>,
): string | null {
  if (activeServerId === null || voice === null) return null;
  if (voice.serverId !== activeServerId) return null;
  // `channel?.type` cobre os dois nãos de uma vez: o canal que desapareceu da
  // store e o canal que existe mas é de texto. Um canal de texto com o nome de
  // uma sala não é uma call, e não pode teletransportar ninguém.
  const channel = channels[voice.channelId];
  if (channel?.type !== "VOICE") return null;
  return roomByVoiceName(channel.name)?.id ?? null;
}

/**
 * A mesma regra, mas para quem TEM corpo no mapa: para onde é que o avatar tem
 * de ir quando a call muda — ou `null` para ficar exactamente onde está.
 *
 * O pedido do Kawã à letra: *"quando eu tô no modo discord e entro numa sala de
 * lá, o meu bonequinho tem que sair da sala que ele estava antes e ir para a
 * nova"*. Entrar numa call pela LISTA do chat não mexia ninguém — o mapa dizia
 * que a pessoa estava em Vendas enquanto a voz dela saía de TI. O avatar É onde
 * se está: se a call mudou de sala, o boneco muda com ela.
 *
 * Fica ao lado da regra do telemóvel porque é a mesma pergunta ("onde deve estar
 * este corpo?") com o mesmo dono; o que muda é quem a executa — ali um
 * `UpdatePosition` ao gateway, aqui o loop do Phaser a pousar o boneco.
 *
 * Os dois `null` são as duas maneiras de NÃO mexer, e ambas são decisões:
 *   • a call não é uma sala do mapa (a "Sala Principal", uma call de outro
 *     servidor, o huddle de corredor) — não há para onde ir;
 *   • já se está na sala certa. Este é o que fecha o ciclo: andar até Vendas
 *     junta à call de Vendas, e a call de Vendas encontra o boneco já em Vendas
 *     e não lhe toca. Sem isto, cada passo até uma sala dava um solavanco para
 *     o meio dela.
 */
export function roomMoveSpot(currentRoomId: string | null, wantRoomId: string | null): { x: number; y: number } | null {
  if (wantRoomId === null || currentRoomId === wantRoomId) return null;
  return standingSpotIn(wantRoomId);
}

/**
 * Quem segura este corpo no mapa — a decisão, sem efeitos.
 *
 * Duas vistas largam o corpo, e nenhuma delas o pode apagar por sua conta: o
 * `OfficeSpace` quando desmonta (carregar em «Discord» destrói-o) e este hook
 * quando deixa de ser o dono (carregar em «Escritório 2D»). Sair do mapa é um
 * `LeaveSpace`, e um `LeaveSpace` a mais era o defeito de 2026-09-04: *«vou pro
 * discord e, mesmo estando na call, o meu personagem some do office 2d»*.
 *
 * Custa mais do que se vê. Para os outros a pessoa desaparece da sala enquanto
 * continua a falar lá dentro — e, do lado do servidor, o `leaveSpace` LIBERTA a
 * tranca quando sai o último ocupante: ir ao chat abria a porta de uma reunião
 * fechada que continua a decorrer.
 *
 * Três donos possíveis, e basta um:
 *   • **o escritório** — está montado e desenha o corpo a 60fps;
 *   • **o telemóvel** — nunca tem escritório, e o corpo dele vive sempre no
 *     mapa (é a razão de este ficheiro existir);
 *   • **a call de uma sala** — estar na call da TI é estar na TI, com ou sem
 *     mapa à frente. É a mesmíssima prova que o servidor já aceita para deixar
 *     voltar a uma sala trancada quem vem do chat a meio da reunião.
 *
 * Trocar de servidor não é nada disto: o escritório que fecha é outro, e o
 * corpo sai do mapa como sempre saiu.
 */
export function corpoNoMapa(p: {
  readonly mesmoServidor: boolean;
  readonly escritorio: boolean;
  readonly phone: boolean;
  readonly salaDaCall: string | null;
}): boolean {
  if (!p.mesmoServidor) return false;
  return p.escritorio || p.phone || p.salaDaCall !== null;
}

/**
 * Liga esse corpo ao gateway. Não desenha nada — chama-se uma vez no esqueleto.
 *
 * Dois efeitos e não um, e a divisão importa: juntá-los fazia com que trocar de
 * call mandasse `LeaveSpace` + `EnterSpace`, e quem estivesse ao computador via o
 * boneco DESAPARECER do mapa e reaparecer noutro sítio, em vez de andar até lá.
 * Separados, a troca de sala é um `UpdatePosition` só — que é o que ela é.
 *
 * Ambos dependem de `linked`: um `send()` antes do socket abrir é descartado em
 * silêncio, e reentrar a cada reconexão é o que devolve o avatar ao mapa depois
 * de o WiFi cair — que num telemóvel é a regra, não a excepção. `EnterSpace` é
 * idempotente do lado do servidor, por isso repetir não custa nada.
 */
export function useOfficePresence(): void {
  const phone = useIsPhone();
  const serverId = useStore((s) => s.activeServerId);
  const linked = useStore((s) => s.connectionStatus === "ready");
  const roomId = useStore((s) => presenceRoomId(s.activeServerId, s.voice, s.channels));
  const escritorio = useStore((s) => s.viewMode === "office");

  /**
   * O corpo é DESTE hook quando não há escritório desenhado a segurá-lo e ainda
   * assim alguém o segura: no telemóvel é sempre (nunca há escritório), no
   * desktop é a vista de chat com a call a manter a pessoa dentro de uma sala.
   *
   * A pergunta deixou de ser "és telemóvel?" para passar a ser "há mapa
   * desenhado?", que é a pergunta certa: um desktop em «Discord» está na
   * mesmíssima situação de um telemóvel — corpo no mundo, ninguém a desenhá-lo.
   */
  const meu = !escritorio && corpoNoMapa({ mesmoServidor: true, escritorio, phone, salaDaCall: roomId });

  // Entrar e sair do espaço. Só isto — a posição é do outro efeito.
  useEffect(() => {
    if (!meu || serverId === null || !linked) return;
    const gw = gatewayClient();
    if (!gw) return;
    gw.enterSpace(serverId);
    return () => {
      // Largar o corpo não é APAGÁ-LO. Carregar em «Escritório 2D» tira-o a este
      // hook e entrega-o ao Phaser no mesmo instante; um LeaveSpace pelo meio
      // fazia toda a gente ver a pessoa piscar para fora e para dentro do mapa —
      // e largava a tranca da sala nesse intervalo. Um cleanup corre já com o
      // estado NOVO na store, por isso a pergunta é sobre o que vem A SEGUIR.
      const estado = useStore.getState();
      const fica = corpoNoMapa({
        mesmoServidor: estado.activeServerId === serverId,
        escritorio: estado.viewMode === "office",
        phone,
        salaDaCall: presenceRoomId(estado.activeServerId, estado.voice, estado.channels),
      });
      // O gateway relê-se na limpeza: entre entrar e sair pode ter havido uma
      // reconexão, e falar com o cliente antigo era falar com um socket morto.
      if (!fica) gatewayClient()?.leaveSpace(serverId);
    };
  }, [meu, phone, serverId, linked]);

  // Onde. Corre a seguir ao efeito de cima na mesma passagem (a ordem é a de
  // declaração), por isso o `EnterSpace` chega sempre primeiro.
  //
  // Repete-se a cada ligação de propósito: o ocupante vive na SESSÃO do socket e
  // uma sessão nova nasce sem ele. Sem esta posição, uma reconexão a meio do
  // chat apagava do mapa quem continua na call — o mesmo sintoma por outra porta.
  useEffect(() => {
    if (!meu || serverId === null || !linked) return;
    const gw = gatewayClient();
    if (!gw) return;
    // Uma sala sem chão livre não existe no mapa que temos — mas se um dia
    // alguém a encher de mobília, a Recepção é a resposta certa, e não um
    // avatar em cima de uma secretária.
    const spot = (roomId === null ? null : standingSpotIn(roomId)) ?? SPAWN;
    // O corpo de um telemóvel NASCE aqui, e o sítio é esta regra. O de um
    // desktop em «Discord» vem POUSADO pelo escritório, e reaproveitar essa
    // posição é o que evita vê-lo levantar-se sozinho da secretária só porque
    // se mudou de vista. Mas só enquanto ela for da sala da call: entrar noutra
    // call pela lista do chat tem de mudar o boneco de sala, senão o mapa dizia
    // Vendas enquanto a voz saía da TI.
    const lembrado = corpoLembrado(serverId);
    const noSitio = lembrado !== null && roomId !== null && roomAt(lembrado.x, lembrado.y)?.id === roomId;
    // Virado para a câmara: é a pose de quem está parado, e um corpo que ninguém
    // desenha está sempre parado — não há teclado nenhum a mudá-la.
    const onde = noSitio && lembrado !== null ? lembrado : { x: spot.x, y: spot.y, facing: "down" as const };
    gw.updatePosition(serverId, Math.round(onde.x), Math.round(onde.y), onde.facing);
  }, [meu, serverId, linked, roomId]);
}
